import {
	LuIcon,
	Menu,
	type MenuCheckboxItemProps,
	type MenuItemMeta,
	type MenuSelectItemProps,
	type SelectAllItemProps,
} from "@overmap-ai/blocks"
import { memo, useCallback } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { genericMemo } from "../../utils/optimization"

// oxlint-disable-next-line react/only-export-components
export const SelectItemTemplate = genericMemo(function <TValue>(props: MenuSelectItemProps<TValue>) {
	const { ref, children, ...rest } = props

	const computedChildren = useCallback(
		(meta: MenuItemMeta) => {
			if (typeof children === "function") {
				return (
					<>
						{children(meta)}
						<Menu.SelectedIndicator className="ml-[auto]">
							<LuIcon icon={modelIconMapping.check} />
						</Menu.SelectedIndicator>
					</>
				)
			} else {
				return (
					<>
						{children}
						<Menu.SelectedIndicator className="ml-[auto]">
							<LuIcon icon={modelIconMapping.check} />
						</Menu.SelectedIndicator>
					</>
				)
			}
		},
		[children],
	)

	return (
		<Menu.SelectItem<TValue> ref={ref} {...rest}>
			{computedChildren}
		</Menu.SelectItem>
	)
})

// oxlint-disable-next-line react/only-export-components
export const MultiSelectItemTemplate = genericMemo(function <TValue>(props: MenuSelectItemProps<TValue>) {
	const { ref, children, ...rest } = props

	const computedChildren = useCallback(
		(meta: MenuItemMeta) => {
			if (typeof children === "function") {
				return (
					<>
						{children(meta)}
						<Menu.SelectedIndicator className="ml-auto">
							<LuIcon icon={modelIconMapping.check} />
						</Menu.SelectedIndicator>
					</>
				)
			} else {
				return (
					<>
						{children}
						<Menu.SelectedIndicator className="ml-auto">
							<LuIcon icon={modelIconMapping.check} />
						</Menu.SelectedIndicator>
					</>
				)
			}
		},
		[children],
	)

	return (
		<Menu.MultiSelectItem<TValue> ref={ref} {...rest}>
			{computedChildren}
		</Menu.MultiSelectItem>
	)
})

export const CheckboxItemTemplate = memo((props: MenuCheckboxItemProps) => {
	const { ref, children, ...rest } = props

	const computedChildren = useCallback(
		(meta: MenuItemMeta) => {
			if (typeof children === "function") {
				return (
					<>
						{children(meta)}
						<Menu.CheckboxItemIndicator className="ml-[auto]">
							<LuIcon icon={modelIconMapping.check} />
						</Menu.CheckboxItemIndicator>
					</>
				)
			} else {
				return (
					<>
						{children}
						<Menu.CheckboxItemIndicator className="ml-[auto]">
							<LuIcon icon={modelIconMapping.check} />
						</Menu.CheckboxItemIndicator>
					</>
				)
			}
		},
		[children],
	)

	return (
		<Menu.CheckboxItem ref={ref} {...rest}>
			{computedChildren}
		</Menu.CheckboxItem>
	)
})
CheckboxItemTemplate.displayName = "CheckboxItemTemplate"

export const SelectAllItemTemplate = memo<SelectAllItemProps>((props) => {
	const { ref, children, ...rest } = props

	const computedChildren = useCallback(
		(meta: { active: boolean; selected: boolean | "indeterminate" }) => {
			if (typeof children === "function") {
				return (
					<>
						{children(meta)}
						<Menu.CheckboxItemIndicator className="ml-[auto]">
							{(state) =>
								state === "indeterminate" ? (
									<LuIcon icon={modelIconMapping.indeterminate} />
								) : (
									<LuIcon icon={modelIconMapping.check} />
								)
							}
						</Menu.CheckboxItemIndicator>
					</>
				)
			} else {
				return (
					<>
						{children}
						<Menu.CheckboxItemIndicator className="ml-[auto]">
							{(state) =>
								state === "indeterminate" ? (
									<LuIcon icon={modelIconMapping.indeterminate} />
								) : (
									<LuIcon icon={modelIconMapping.check} />
								)
							}
						</Menu.CheckboxItemIndicator>
					</>
				)
			}
		},
		[children],
	)

	return (
		<Menu.SelectAllItem ref={ref} {...rest}>
			{computedChildren}
		</Menu.SelectAllItem>
	)
})
SelectAllItemTemplate.displayName = "SelectAllItemTemplate"
